import { useEffect, useMemo, useState } from "react";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import { FIRESTORE_CAMPAIGNS_TABLE } from "constants/FirestoreConstant";

export const useCampaigns = (options = {}) => {
  const { shopSource = "all" } = options;
  const [vceCampaigns, setVceCampaigns] = useState([]);
  const [hvoCampaigns, setHvoCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const unsubscribers = [];

    try {
      if (shopSource === "all" || shopSource === "VCE") {
        const unsubscribeVce = vceFirestore
          .collection(FIRESTORE_CAMPAIGNS_TABLE)
          .orderBy("createdAt", "desc")
          .onSnapshot(
            (snapshot) => {
              const data = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
                shopSource: "VCE",
              }));
              setVceCampaigns(data);
              setIsLoading(false);
            },
            (err) => {
              console.error("Error fetching VCE campaigns:", err);
              setError(err);
              setIsLoading(false);
            },
          );
        unsubscribers.push(unsubscribeVce);
      } else {
        setVceCampaigns([]);
      }

      if (shopSource === "all" || shopSource === "HVO") {
        const unsubscribeHvo = hvoFirestore
          .collection(FIRESTORE_CAMPAIGNS_TABLE)
          .orderBy("createdAt", "desc")
          .onSnapshot(
            (snapshot) => {
              const data = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
                shopSource: "HVO",
              }));
              setHvoCampaigns(data);
              setIsLoading(false);
            },
            (err) => {
              console.error("Error fetching HVO campaigns:", err);
              setError(err);
              setIsLoading(false);
            },
          );
        unsubscribers.push(unsubscribeHvo);
      } else {
        setHvoCampaigns([]);
      }

      if (unsubscribers.length === 0) {
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Error setting up campaign listeners:", err);
      setError(err);
      setIsLoading(false);
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [shopSource]);

  const campaigns = useMemo(() => {
    const combined = [...vceCampaigns, ...hvoCampaigns];
    return combined.sort((a, b) => {
      const aTime = a.createdAt?.seconds || a.createdAt || 0;
      const bTime = b.createdAt?.seconds || b.createdAt || 0;
      return bTime - aTime;
    });
  }, [vceCampaigns, hvoCampaigns]);

  const refreshCampaigns = () => {
    setIsLoading(true);
    setError(null);
  };

  return {
    campaigns,
    vceCampaigns,
    hvoCampaigns,
    isLoading,
    error,
    refreshCampaigns,
  };
};

export const useCampaign = (campaignId, campaignShopSource) => {
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignId || !campaignShopSource) {
      setIsLoading(false);
      return;
    }

    const fetchCampaign = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const firestore = campaignShopSource === "VCE" ? vceFirestore : hvoFirestore;
        const doc = await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).doc(campaignId).get();

        if (doc.exists) {
          setCampaign({
            id: doc.id,
            ...doc.data(),
            shopSource: campaignShopSource,
          });
        } else {
          setError(new Error("Campaign not found"));
        }
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId, campaignShopSource]);

  return { campaign, isLoading, error };
};
